import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddRemoveIcon from '@sharedCR/common/AddRemoveIcon';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { LESection } from 'constants/loanApplication';
import { COLOR_PALETTE } from 'constants/ui';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import { selectFeeTypesOptionsWithoutSellerPaid } from 'store/system/selectors';

import { LoanFeesTestIds } from '../../../../../../../LoanFees.testIds';
import { addButtonStyles } from '../../../../AccordionDetailsView/FeeTable/FeeTable.styles';

import { prepaidTableStyles } from './PrepaidTable.styles';
import PrepaidTableRow from './PrepaidTableRow';

const PrepaidTable = ({
  data,
  onAddFee,
  onDeleteFee,
  setIsLoading,
  isLoading,
  meta: {
    handleChange,
    validateField,
    validateForm,
    setInForm,
    setForm,
    validationErrors: vldErrors,
  },
}) => {
  const feesOptions = useSelector(selectFeeTypesOptionsWithoutSellerPaid);

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const filteredLoanFeesOptions = useMemo(
    () =>
      feesOptions.filter(
        option =>
          !(
            option.isCustomDisabled ||
            option.isDeleted ||
            option.leSection !== LESection.PREPAIDS ||
            data.some(({ feeTypeId }) => feeTypeId?.value === option?.value)
          ),
      ),
    [data, feesOptions],
  );

  return (
    <>
      <TruncatedProvider />

      <TableContainer
        sx={prepaidTableStyles}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        <Table>
          <TableHead>
            <TableRow className="table-header">
              <TableCell>Prepaid Name</TableCell>
              <TableCell>Fee Provider</TableCell>
              <TableCell>Annual Amount</TableCell>
              <TableCell>Months</TableCell>
              <TableCell>Total Collected</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map(({ feeId, ...fee }, index) => {
              return (
                <PrepaidTableRow
                  {...fee}
                  feeId={feeId}
                  filteredLoanFeesOptions={filteredLoanFeesOptions}
                  handleChange={handleChange}
                  index={index}
                  isLoading={isLoading}
                  key={feeId}
                  pendingChangesActionType={fee.pendingChangesActionType}
                  setForm={setForm}
                  setInForm={setInForm}
                  setIsLoading={setIsLoading}
                  validateField={validateField}
                  validateForm={validateForm}
                  vldErrors={vldErrors}
                  onDeleteFee={onDeleteFee}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="flex justify-start my-12">
        <Button
          data-testid={LoanFeesTestIds.PREPAID_TABLE_ADD_BUTTON}
          disabled={isLoading}
          sx={addButtonStyles}
          variant="text"
          onClick={onAddFee}
        >
          <AddRemoveIcon color={COLOR_PALETTE.BRAND[6]} />
          <Typography variant="regular14">Add Fee</Typography>
        </Button>
      </div>
    </>
  );
};

PrepaidTable.propTypes = {
  ...WithLoadingPropTypes,
  data: PropTypes.array.isRequired,
  meta: PropTypes.shape({
    handleChange: PropTypes.func.isRequired,
    validateField: PropTypes.func.isRequired,
    validateForm: PropTypes.func.isRequired,
    setInForm: PropTypes.func.isRequired,
    setForm: PropTypes.func.isRequired,
    setIsTouched: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    setEditedSection: PropTypes.func.isRequired,
  }).isRequired,
  onAddFee: PropTypes.func.isRequired,
  onDeleteFee: PropTypes.func.isRequired,
};

export default PrepaidTable;
